import React, { useState, useEffect, useCallback, useRef } from "react";
import { PokeBall } from "./Objects3D";
import { useBall } from "./ballThemes";

/**
 * Places a pokéball on the last point of the chart's line.
 *
 * The end point isn't a fixed position — it moves with the data, the timeframe
 * and the container width — so CSS can't express it. This measures the rendered
 * recharts path instead: take the point at the end of its length, map it
 * through the SVG's screen matrix, and convert to coordinates relative to the
 * wrapper. That works regardless of recharts' internal margins or scaling.
 *
 * Re-measures on resize and whenever the path data changes (timeframe switch,
 * price refresh). The chart component itself is never touched.
 *
 * @param {{containerRef: React.RefObject<HTMLElement>, size?: number}} props
 */
export function ChartEndMarker({ containerRef, size = 30 }) {
  const ball = useBall();
  const [pos, setPos] = useState(null);
  const rafRef = useRef(0);

  const measure = useCallback(() => {
    const host = containerRef.current;
    if (!host) return;

    // The visible chart is whichever wrapper is laid out — the components render
    // a mobile and a desktop copy, one of them display:none (zero-size rect).
    const wrappers = Array.from(host.querySelectorAll(".recharts-wrapper"));
    const wrapper = wrappers.find((w) => w.getBoundingClientRect().width > 0);
    if (!wrapper) return setPos(null);

    const path = wrapper.querySelector(".recharts-line-curve, .recharts-curve");
    if (!path || typeof path.getPointAtLength !== "function") return setPos(null);

    let len = 0;
    try {
      len = path.getTotalLength();
    } catch {
      return setPos(null);
    }
    if (!len) return setPos(null);

    const p = path.getPointAtLength(len);
    const ctm = path.getScreenCTM();
    if (!ctm) return setPos(null);

    // SVG user space → screen → wrapper-relative.
    const screenX = ctm.a * p.x + ctm.c * p.y + ctm.e;
    const screenY = ctm.b * p.x + ctm.d * p.y + ctm.f;
    const box = wrapper.getBoundingClientRect();
    const hostBox = host.getBoundingClientRect();

    setPos({
      left: screenX - hostBox.left,
      top: screenY - hostBox.top,
      ok: screenX >= box.left - 1 && screenX <= box.right + 1,
    });
  }, [containerRef]);

  /** Coalesce bursts of mutations into one measurement per frame. */
  const schedule = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(measure);
  }, [measure]);

  useEffect(() => {
    const host = containerRef.current;
    if (!host) return undefined;

    schedule();
    // The draw-in animation means the path's length grows after mount.
    const settle = setTimeout(schedule, 1300);

    const ro = new ResizeObserver(schedule);
    ro.observe(host);

    // Timeframe switches and price refreshes replace the path's `d`.
    const mo = new MutationObserver(schedule);
    mo.observe(host, { subtree: true, childList: true, attributes: true, attributeFilter: ["d"] });

    window.addEventListener("resize", schedule);
    return () => {
      cancelAnimationFrame(rafRef.current);
      clearTimeout(settle);
      ro.disconnect();
      mo.disconnect();
      window.removeEventListener("resize", schedule);
    };
  }, [containerRef, schedule]);

  if (!pos || !pos.ok) return null;

  return (
    <div
      className="ce-raw"
      aria-hidden="true"
      style={{
        position: "absolute",
        left: pos.left,
        top: pos.top,
        width: size,
        height: size,
        marginLeft: -size / 2,
        marginTop: -size / 2,
        pointerEvents: "none",
        zIndex: 4,
      }}
    >
      <PokeBall d={size} ball={ball} style={{ boxShadow: "0 0 0 2px #1b1b1b, 2px 2px 0 rgba(0,0,0,0.28)" }} />
    </div>
  );
}

export default ChartEndMarker;
